import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CAlert, CBadge, CCard, CCardBody, CCardFooter, CCol, CRow, CSpinner } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import { useMyMemberships } from "../../api/hooks/useMemberships";
import { MembershipRequestStatus, useMyMembershipRequests } from "../../api/hooks/useMembershipRequests";
import { isExternalTier, type MembershipTierDto, useMembershipTiers } from "../../api/hooks/useMembershipTiers";
import { formatDayMonth } from "../../lib/dates";
import { formatPriceUsd } from "../../lib/money";
import { membershipDetailsPath } from "../../routes/route-paths";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { countStrategiesByIds } from "../trading-strategies/strategySides";
import { MembershipBadge } from "../trading-strategies/components/MembershipBadge";

/** One card's worth of a membership, from its live tier, or from the customer's membership or request. */
interface MembershipCardItem {
  tierId: string;
  name: string;
  platformName: string;
  badgeColour: string;
  badgeLetters: string | null;
  description: string | null;
  strategyCount: number | null;
  price: string | null;
  pill: ReactNode;
  note: string | null;
}

/**
 * Every membership: the customer's own first (held, awaiting approval or declined), then ours to buy, then the
 * ones on other platforms they could tell us about.
 */
export function BrowseMemberships() {
  const { t } = useTranslation("memberships");
  const tiers = useMembershipTiers();
  const memberships = useMyMemberships();
  const catalogue = useTradingStrategies().data ?? [];
  const requests = useMyMembershipRequests();

  const isLoading = tiers.isLoading || memberships.isLoading || requests.isLoading;
  const loadError = tiers.error ?? memberships.error ?? requests.error;

  const tierById = new Map((tiers.data ?? []).map((tier) => [tier.id, tier]));

  const held: MembershipCardItem[] = (memberships.data ?? []).map((membership) => {
    const tier = tierById.get(membership.membershipTierId);
    return {
      tierId: membership.membershipTierId,
      name: membership.tierName,
      platformName: membership.platformName,
      badgeColour: membership.badgeColour,
      badgeLetters: membership.badgeLetters,
      description: tier?.description ?? null,
      strategyCount: countStrategiesByIds(membership.tradingStrategyIds, catalogue),
      price: null,
      pill: membership.activeTo ? (
        <CBadge color="warning" shape="rounded-pill">
          {t("status.endsOn", { date: formatDayMonth(membership.activeTo) })}
        </CBadge>
      ) : (
        <CBadge color="success" shape="rounded-pill">
          {t("status.active")}
        </CBadge>
      ),
      note: null,
    };
  });

  // Awaiting approval before declined: waiting is what the customer is most likely checking on.
  const asked: MembershipCardItem[] = [...(requests.data ?? [])]
    .sort((a, b) => a.status - b.status)
    .map((request) => {
      const tier = tierById.get(request.membershipTierId);
      const declined = request.status === MembershipRequestStatus.Declined;
      return {
        tierId: request.membershipTierId,
        name: request.tierName,
        platformName: request.platformName,
        badgeColour: request.badgeColour,
        badgeLetters: request.badgeLetters,
        description: tier?.description ?? null,
        strategyCount: tier ? countStrategiesByIds(tier.tradingStrategyIds, catalogue) : null,
        price: null,
        pill: declined ? (
          <CBadge color="danger" shape="rounded-pill">
            {t("status.declined")}
          </CBadge>
        ) : (
          <CBadge color="warning" shape="rounded-pill">
            {t("status.pending")}
          </CBadge>
        ),
        note: declined
          ? t("browse.declinedMeta", {
              date: formatDayMonth(request.decidedAt ?? request.requestedAt),
              reason: request.declineReason,
            })
          : t("browse.pendingMeta", { date: formatDayMonth(request.requestedAt) }),
      };
    });

  const yours = [...held, ...asked];
  const yourTierIds = new Set(yours.map((item) => item.tierId));
  // Ours first, then the other platforms in the order the tiers arrive.
  const others = (tiers.data ?? [])
    .filter((tier) => !yourTierIds.has(tier.id))
    .sort((a, b) => Number(isExternalTier(a)) - Number(isExternalTier(b)));
  const platforms = [...new Map(others.map((tier) => [tier.platform.id, tier.platform])).values()];
  const nothing = yours.length === 0 && others.length === 0;

  // Ours are bought here, so they show what they cost; another platform's do not.
  const priceOf = (tier: MembershipTierDto) =>
    !isExternalTier(tier) && tier.monthlyPriceUsd !== null && tier.yearlyPriceUsd !== null
      ? t("browse.price", {
          monthly: formatPriceUsd(tier.monthlyPriceUsd),
          yearly: formatPriceUsd(tier.yearlyPriceUsd),
        })
      : null;

  return (
    <div>
      <h1 className="mb-1">{t("browse.title")}</h1>
      <p className="text-body-secondary mb-4">{t("browse.description")}</p>

      {isLoading && (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {!isLoading && loadError && <CAlert color="danger">{t("browse.loadError")}</CAlert>}

      {!isLoading && !loadError && nothing && <CAlert color="info">{t("browse.none")}</CAlert>}

      {!isLoading && !loadError && yours.length > 0 && (
        <section className="mb-5" aria-labelledby="memberships-yours">
          <h2 id="memberships-yours" className="h4 mb-3">
            {t("browse.yours")}
          </h2>
          <CRow xs={{ gutter: 3 }}>
            {yours.map((item) => (
              <CCol key={item.tierId} md={6} xl={4}>
                <MembershipCard item={item} withPlatform />
              </CCol>
            ))}
          </CRow>
        </section>
      )}

      {!isLoading && !loadError && others.length > 0 && (
        <>
          {yours.length > 0 && <h2 className="h4 mb-3">{t("browse.more")}</h2>}
          {platforms.map((platform) => (
            <section key={platform.id} className="mb-4" aria-labelledby={`platform-${platform.id}`}>
              <h3
                id={`platform-${platform.id}`}
                className="h6 text-uppercase fw-bold text-body-secondary d-flex align-items-center gap-2 mb-3"
              >
                <MembershipBadge
                  membership={{
                    badgeColour: platform.badgeColour,
                    badgeLetters: platform.badgeLetters,
                    platformName: platform.name,
                  }}
                />
                {platform.name}
              </h3>
              <CRow xs={{ gutter: 3 }}>
                {others
                  .filter((tier) => tier.platform.id === platform.id)
                  .map((tier) => (
                    <CCol key={tier.id} md={6} xl={4}>
                      <MembershipCard item={fromTier(tier, priceOf(tier), countStrategiesByIds(tier.tradingStrategyIds, catalogue))} />
                    </CCol>
                  ))}
              </CRow>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

function fromTier(tier: MembershipTierDto, price: string | null, strategyCount: number): MembershipCardItem {
  return {
    tierId: tier.id,
    name: tier.name,
    platformName: tier.platform.name,
    badgeColour: tier.platform.badgeColour,
    badgeLetters: tier.platform.badgeLetters,
    description: tier.description,
    strategyCount,
    price,
    pill: null,
    note: null,
  };
}

function MembershipCard({ item, withPlatform = false }: { item: MembershipCardItem; withPlatform?: boolean }) {
  const { t } = useTranslation("memberships");

  return (
    <Link to={membershipDetailsPath(item.tierId)} className="card-link">
      <CCard>
        <CCardBody className="d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <h3 className="h5 mb-0 d-flex align-items-center flex-wrap gap-2">
              {withPlatform && <MembershipBadge membership={item} />}
              {withPlatform ? `${item.platformName} · ${item.name}` : item.name}
            </h3>
            {item.pill}
          </div>
          {item.description && <p className="text-body-secondary mb-0">{item.description}</p>}
          {item.price && <p className="fw-semibold mb-0">{item.price}</p>}
          {item.note && <p className="small text-body-secondary mb-0 text-break">{item.note}</p>}
        </CCardBody>
        <CCardFooter className="bg-body-tertiary d-flex align-items-center gap-2">
          <small className="text-body-secondary flex-grow-1">
            {item.strategyCount !== null && t("browse.strategies", { count: item.strategyCount })}
          </small>
          <CIcon icon={cilChevronRight} size="xl" className="card-link-chevron" aria-hidden />
        </CCardFooter>
      </CCard>
    </Link>
  );
}

export default BrowseMemberships;
